import { Link, Typography } from '@material-ui/core';
import { Alert, Button } from '@superdispatch/ui-lab';
import { Inline, Stack } from '@superdispatch/ui';
import { useEffect, useState } from 'react';
import { INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL } from 'shared/modules/superpay/instantTransferAccessLostHelpArticleUrl';
import { InstantTransferMultipleSettleDialog } from 'shared/modules/superpay/instant-transfer-settle/InstantTransferMultipleSettleDialog';
import { LinkAnchor, LinkButton } from 'shared/routing/Links';
import {
  trackInstantTransferRecoveryDismissed,
  trackInstantTransferRecoveryBannerViewed,
  trackInstantTransferRecoveryOrderLinkClicked,
  trackInstantTransferRecoverySettleCTAClicked,
} from './InstantTransferRecoveryAnalytics';

interface InstantTransferNotificationPastDueProps {
  notificationContext: {
    status: string;
    loads_count: number;
    load_id: string | null;
    load_guid: string | null;
  };
  message: string;
  onClose?: () => void;
}

export function InstantTransferNotificationPastDue({
  notificationContext,
  message,
  onClose,
}: InstantTransferNotificationPastDueProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isMultiple = notificationContext.loads_count > 1;
  const bannerState = isMultiple ? 'access_lost_multi' : 'access_lost';
  const loadId = notificationContext.load_id;
  const loadsCount = notificationContext.loads_count;

  useEffect(() => {
    trackInstantTransferRecoveryBannerViewed({
      banner_state: bannerState,
      order_id: isMultiple ? null : loadId,
      orders_count: isMultiple ? loadsCount : null,
    });
  }, [bannerState, isMultiple, loadId, loadsCount]);

  return (
    <>
      {!isDialogOpen && (
        <Alert
          severity="critical"
          onClose={() => {
            trackInstantTransferRecoveryDismissed({
              banner_state: bannerState,
              order_id: isMultiple ? null : loadId,
              orders_count: isMultiple ? loadsCount : null,
            });
            onClose?.();
          }}
        >
          <Stack space="xxsmall">
            <Typography variant="body1">{message}</Typography>
            {isMultiple ? (
              <Typography>
                There are <b>{loadsCount} loads</b> with shipper payment issues.
                Settle the outstanding balance for all of these loads to restore
                your access to Instant Transfers.
              </Typography>
            ) : (
              <Typography>
                Instant Transfer was issued for{' '}
                <LinkAnchor
                  to={`/loads/${notificationContext.load_guid}`}
                  onClick={() => {
                    trackInstantTransferRecoveryOrderLinkClicked({
                      banner_state: 'access_lost',
                      order_id: loadId ?? '',
                    });
                  }}
                >
                  {loadId}
                </LinkAnchor>
                . You're responsible if shipper payment isn't collected.
                <br /> Settle now to regain access to Instant Transfer.
              </Typography>
            )}

            <Inline space="small" verticalAlign="bottom">
              {isMultiple ? (
                <Button
                  size="small"
                  variant="neutral"
                  onClick={() => {
                    trackInstantTransferRecoverySettleCTAClicked({
                      banner_state: 'access_lost_multi',
                      order_id: null,
                      orders_count: loadsCount,
                    });
                    setIsDialogOpen(true);
                  }}
                >
                  Settle Payment
                </Button>
              ) : (
                <LinkButton
                  size="small"
                  variant="neutral"
                  to={`/loads/${notificationContext.load_guid}/instant-transfer-settle?source=banner`}
                  onClick={() => {
                    trackInstantTransferRecoverySettleCTAClicked({
                      banner_state: 'access_lost',
                      order_id: loadId,
                      orders_count: null,
                    });
                    onClose?.();
                  }}
                >
                  Settle Payment
                </LinkButton>
              )}
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL}
                onClick={() => {
                  onClose?.();
                }}
              >
                Learn more
              </Link>
            </Inline>
          </Stack>
        </Alert>
      )}
      <InstantTransferMultipleSettleDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => onClose?.()}
      />
    </>
  );
}
